import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { ActionModal } from "@/app/ActionModal";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { accounts, customers, profitGuards, type ProfitGuard, type ProfitGuardInput } from "@/app/master";
import { accountOptions, loadingOf, useList } from "@/app/queries";
import { Alert, Button, Dropdown, EmptyState, formatTime, Input as TextInput, Money, PageHeader, Pill, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { ProfitGuardForm } from "./ProfitGuardForm";
import { guardScope, profitGuardDraft, profitGuardInput } from "./profitGuardDraft";

/** 利润保护（决策 D70）：按账号 + MCC + MNC 设亏损上限，cron 每分钟累计净利润，达到上限就熔断、该账号发往这个运营商的消息按无路由失败；
 * 新建与编辑在抽屉里，熔断后手工重置恢复。 */
export function ProfitGuards() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ row: ProfitGuard | null } | null>(null);
  const [resetting, setResetting] = useState<ProfitGuard | null>(null);
  const list = useList("profit-guards", profitGuards);
  const options = accountOptions(useList("accounts", accounts).data, useList("customers", customers).data);
  const accountName = (id: number) => options.find((o) => o.value === String(id))?.label ?? `#${id}`;
  const keyword = q.trim().toLowerCase();
  const shown = (list.data ?? []).filter((g) => !keyword || `${accountName(g.account_id)} ${guardScope(g)}`.toLowerCase().includes(keyword));

  return (
    <>
      <PageHeader title="利润保护" description="按账号 + MCC + MNC 每分钟累计净利润，净亏损达到上限即停发这个运营商，客户收到无路由（012）；重置后从零统计。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing({ row: null })}>新建规则</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar right={<ToolbarCount>{shown.length} 条</ToolbarCount>}>
        <TextInput size="sm" className="ax-search" placeholder="搜索账号或 MCC" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {shown.length ? (
          <Table>
            <thead><tr><Th w={220}>账号</Th><Th w="op">MCC-MNC</Th><Th numeric w="money">允许最大亏损</Th><Th numeric w="money">累计净利润</Th><Th w={200}>状态</Th><Th w={160}>统计起点</Th><Th w="wide">备注</Th><Th /></tr></thead>
            <tbody>
              {shown.map((g) => (
                <tr key={g.id}>
                  <td>{accountName(g.account_id)}</td><td className="ax-mono">{guardScope(g)}</td>
                  <td className="ax-num"><Money micro={g.loss_limit} currency="USD" /></td><td className="ax-num"><Money micro={g.net_profit} currency="USD" /></td>
                  <td><GuardStatus g={g} /></td><td className="ax-mono">{formatTime(g.counted_since, false)}</td><td className="ax-secondary">{g.note}</td>
                  <td>
                    <Dropdown iconOnly label="更多操作" variant="ghost" size="sm" align="end" items={canWrite ? [
                      { key: "edit", label: "编辑", onSelect: () => setEditing({ row: g }) },
                      "sep",
                      { key: "reset", label: "重置累计", onSelect: () => setResetting(g) },
                    ] : [{ key: "edit", label: "查看", onSelect: () => setEditing({ row: g }) }]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !list.isPending && <EmptyState title="没有利润保护规则">新建规则后，从保存时起统计这个账号在这个运营商上的净利润。</EmptyState>
        )}
      </TableWrap>
      {editing && (
        <EntityDrawer title={editing.row ? `编辑利润保护 ${accountName(editing.row.account_id)} · ${guardScope(editing.row)}` : "新建利润保护规则"}
          initial={profitGuardDraft(editing.row ?? undefined)} convert={profitGuardInput}
          save={(body: ProfitGuardInput) => (editing.row ? profitGuards.update(editing.row.id, body) : profitGuards.create(body))}
          invalidate={["profit-guards"]} message={editing.row ? "已更新利润保护规则" : "已新建利润保护规则"} deletion={editing.row ? deleteTarget("利润保护规则", `${accountName(editing.row.account_id)} · ${guardScope(editing.row)}`, profitGuards, editing.row.id, ["profit-guards"]) : undefined} onClose={() => setEditing(null)}>
          {(f) => <ProfitGuardForm draft={f.draft} errors={f.errors} set={f.set} isNew={!editing.row} accounts={options} />}
        </EntityDrawer>
      )}
      {resetting && <ResetModal row={resetting} name={`${accountName(resetting.account_id)} · ${guardScope(resetting)}`} onClose={() => setResetting(null)} />}
    </>
  );
}

/** 规则的状态：停用、已熔断（带熔断时间）、统计中。停用时仍标出熔断：重新启用后照样拦截，要先重置。 */
function GuardStatus({ g }: { g: ProfitGuard }) {
  if (g.status !== "active") return <Pill>{g.tripped_at ? "停用（已熔断）" : "停用"}</Pill>;
  if (g.tripped_at) return <Pill tone="danger" title={`熔断于 ${formatTime(g.tripped_at)}`}>已熔断 {formatTime(g.tripped_at, false)}</Pill>;
  return <Pill tone="success">统计中</Pill>;
}

/** 重置的二次确认：累计清零、从现在重新统计、解除熔断，进审计（决策 D70）。 */
function ResetModal({ row, name, onClose }: { row: ProfitGuard; name: string; onClose: () => void }) {
  return (
    <ActionModal title={`重置利润保护 ${name}`} confirmLabel="重置" action={() => profitGuards.reset(row.id)} invalidate={["profit-guards"]} message={`已重置 ${name}`} onClose={onClose}
      note={<>累计净利润清零，从现在起重新统计{row.tripped_at ? "，并解除熔断，这个账号几秒内恢复往这个运营商发送" : ""}。此操作写入审计日志。</>} />
  );
}
